import { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import { useVocab } from '@fhnw/components/VocabContext/useVocab';
import type { RoleProps } from '@fhnw/types/role';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { getListingImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import type { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Listing } from '@designsystem/stories/04-organisms/Listing';

export interface BlockDataProps extends BlockWrapperDataProps {
  contacts?: RoleProps[];
}

interface ViewProps {
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const functionVocab: Array<{ value: string; label: string }> = useVocab(
    'collective.taxonomy.function',
  );
  const functionValues: Record<string, string> = {};
  functionVocab.forEach(({ value, label }) => {
    functionValues[value] = label;
  });

  let updated = false;
  if (!data?.block_title) {
    data.block_title = intl.formatMessage(messages.Title);
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let contacts = data?.contacts;
  if (!contacts || !contacts.length) {
    contacts = content?.contacts;
  }

  const items: CardProps[] = [];

  (contacts || []).forEach((role) => {
    const contact = role?.contact as NormalizableContent;
    const func = normalizeContent(role?.function, false);

    if (!contact) {
      return;
    }

    const item = transformToCard(
      contact,
      intl,
      'person',
      undefined,
      undefined,
      false,
      getListingImageSizes('person', undefined, true),
    );

    if (
      func &&
      (func as string) in functionValues &&
      functionValues[func as string] !== intl.formatMessage(messages.Title)
    ) {
      item.title = (
        <>
          {item.title} <small>– {functionValues[func as string]}</small>
        </>
      );
    }

    items.push(item);
  });

  if (!props?.isEdit && !items.length) {
    return null;
  }

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      <Listing type="person" list={true} items={items} />
    </BlockWrapper>
  );
};

export default View;
